import Collapse from '@material-ui/core/Collapse';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import styled from 'styled-components';
import { colors } from '~/constants/colors';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  campaignNames,
  CTA_APP_CLICKED,
  CTA_DISMISS_CLICKED,
  DOWNLOAD_DRIVER_CAROUSEL,
  DOWNLOAD_DRIVER_DISMISS,
  DOWNLOAD_DRIVER_IMPRESSION,
  LOADED,
} from '~/core/analytics/events';
import { isMobile, isNativeApp, useUserAgent } from '~/core/util/device';
import { ButtonVariations } from '../SolvPatternLibrary/Button';
import StarRating from '../SolvPatternLibrary/StarRating';
import {
  Close,
  CopyContainer,
  Icon,
  Root,
  SubTitleCopy,
  TitleCopy,
  TryAppButton,
} from './sharedStyles';
import {
  downloadDriverFeatureFlagCheck,
  getNativeStoreDownloadLink,
  hasAppUtmSource,
} from './util';
import DownloadDriverCarousel from './DownloadDriverCarousel';
import SmartBannerIosMetaTag from '~/components/DownloadDriver/SmartBannerIosMetaTag';

// TODO: once we have an app in the google play store, change this to being different for ios/android
const DOWNLOAD_DRIVER_DISMISS_COOKIE = 'download driver dismiss cookie for ios';
const SEVEN_DAYS_IN_MILLISECONDS = 604800000;

const Rating = styled(StarRating).withConfig({ componentId: 'sc-zvs0t0-0' })`
  justify-self: flex-end;
  height: 12px;
  line-height: 12px;
  font-size: 12px;
`;

const variants = {
  default: { title: 'Get to great care, faster', subtitle: 'Book same-day appointments' },
  onlineWaitlist: {
    title: 'Quality care is a tap away',
    subtitle: 'Book your next visit even quicker',
  },
  rebooking: { title: 'Keep great care a tap away', subtitle: 'Track perks, re-book instantly' },
  waitlist: {
    title: 'Skip the line, get care faster',
    subtitle: 'Book your next visit with one tap',
  },
  reviewPage: {
    title: 'Skip the line, get care faster',
    subtitle: 'Book same-day appointments',
  },
  account: {
    title: 'Keep great care a tap away ',
    subtitle: 'Book same-day appointments',
  },
  contactless: {
    title: 'Skip the line, get care faster',
    subtitle: 'Book your next visit with one tap',
  },
  testResults: {
    title: 'Keep great care a tap away',
    subtitle: 'Access test results instantly',
  },
};

type Props = {
  /** The tracking campaign string to use */
  campaignName?: string;

  /** Controls the copy that is shown */
  variant?: keyof typeof variants;

  /** The title field override (takes precedence over variant) */
  title?: string;

  /** The subtitle field override (takes precedence over variant) */
  subtitle?: string;

  isCarousel?: boolean;
};

/**
 * App download banner component. Conditionally shows the download banner on mobile devices given the right context.
 *
 * Has a default set of variants which control copy. Can be overridden by the `title` and `subtitle` props.
 */
const DownloadDriver = ({
  campaignName = campaignNames.DEFAULT_CAMPAIGN,
  title,
  subtitle,
  variant = 'default',
  isCarousel,
}: Props) => {
  const [visible, setVisible] = useState(true);
  const [cookies, setCookies] = useCookies();

  const [isDownloadDriverCarouselVisible, setIsDownloadDriverCarouselVisible] = useState(true);
  const userAgent = useUserAgent();

  const shouldShow = useMemo(() => {
    if (
      isNativeApp() ||
      !downloadDriverFeatureFlagCheck() ||
      !isMobile(userAgent) ||
      hasAppUtmSource()
    ) {
      return false;
    }

    const driverCookie = cookies[DOWNLOAD_DRIVER_DISMISS_COOKIE];
    return !driverCookie;
  }, [cookies, userAgent]);

  const trackingProperties = useMemo(
    () => ({
      url: typeof window !== 'undefined' && window.location,
      campaignName,
    }),
    [campaignName]
  );

  const handleCarouselCloseClick = () => {
    analyticsTrackEvent(DOWNLOAD_DRIVER_CAROUSEL, {
      action: CTA_DISMISS_CLICKED,
      ...trackingProperties,
    });
    setIsDownloadDriverCarouselVisible(false);
    setCookies(DOWNLOAD_DRIVER_DISMISS_COOKIE, 'dismissed', {
      path: '/',
      expires: new Date(+new Date() + SEVEN_DAYS_IN_MILLISECONDS),
    });
  };

  const setCookie = useCallback(() => {
    setCookies(DOWNLOAD_DRIVER_DISMISS_COOKIE, 'dismissed', {
      path: '/',
      expires: new Date(+new Date() + SEVEN_DAYS_IN_MILLISECONDS),
    });
  }, [setCookies]);

  useEffect(() => {
    if (shouldShow) {
      analyticsTrackEvent(DOWNLOAD_DRIVER_IMPRESSION, trackingProperties);
    }
  }, [shouldShow, trackingProperties]);

  useEffect(() => {
    if (isCarousel && shouldShow) {
      analyticsTrackEvent(DOWNLOAD_DRIVER_CAROUSEL, { action: LOADED, ...trackingProperties });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = useCallback(() => {
    analyticsTrackEvent(DOWNLOAD_DRIVER_IMPRESSION, trackingProperties);
    window.location.href = getNativeStoreDownloadLink(campaignName);
  }, [campaignName, trackingProperties]);

  const handleCarouselAppClick = useCallback(() => {
    analyticsTrackEvent(DOWNLOAD_DRIVER_CAROUSEL, {
      action: CTA_APP_CLICKED,
      ...trackingProperties,
    });
    window.location.href = getNativeStoreDownloadLink(campaignName);
  }, [campaignName, trackingProperties]);

  const handleDismiss = useCallback(() => {
    analyticsTrackEvent(DOWNLOAD_DRIVER_DISMISS, trackingProperties);
    setVisible(false);
  }, [trackingProperties]);

  if (!shouldShow) return <SmartBannerIosMetaTag />;

  if (isCarousel) {
    return (
      <>
        <SmartBannerIosMetaTag />
        <DownloadDriverCarousel
          handleAppClick={handleCarouselAppClick}
          onClose={handleCarouselCloseClick}
          visible={isDownloadDriverCarouselVisible}
        />
      </>
    );
  }

  return (
    <>
      <SmartBannerIosMetaTag />
      <Collapse in={visible} onExited={setCookie} timeout="auto" unmountOnExit>
        <Root>
          <Close onClick={handleDismiss} />
          <Icon />

          <CopyContainer>
            <div>
              <TitleCopy>{title ?? variants[variant].title}</TitleCopy>
              <SubTitleCopy>{subtitle ?? variants[variant].subtitle}</SubTitleCopy>
            </div>

            <Rating
              identifier="app-rating"
              primaryColor={colors.appStoreSwagger}
              rating={5}
              size={12}
              spacing="6px"
            />
          </CopyContainer>

          <TryAppButton onClick={handleClick} variation={ButtonVariations.pinkTertiary}>
            GET APP
          </TryAppButton>
        </Root>
      </Collapse>
    </>
  );
};

export default DownloadDriver;
